'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton } from '@order-up/common';
import { Location as LocationRecord, Tenant } from '@order-up/models';
import React, { useEffect } from 'react';
import { createLogger } from '../utils/logger';
import { useSelection } from '../contexts/SelectionContext';
import { useAuth, useSignOut } from '../hooks/useAuth';
import { useGetUserByUid, useUpdateUser } from '../hooks/useUsers';
import { getCurrentUserToken } from '../services/auth';
import { LocationSelector } from './LocationSelector';
import { TenantSelector } from './TenantSelector';

const logger = createLogger('SelectionWizard');

export interface SelectionWizardProps {
  /** Callback when tenant and restaurant are selected */
  onComplete?: (data: { tenant: Tenant; restaurant: LocationRecord; token?: string }) => void;
  /** URL to redirect to after selection (external auth flow) */
  returnUrl?: string;
  /** Custom redirect handler */
  onRedirect?: (url: string) => void;
  /** Update user last login time */
  updateLastLogin?: boolean;
  /** Custom tenant selector props */
  tenantSelectorProps?: {
    title?: string;
    description?: string;
    emptyMessage?: string;
  };
  /** Custom restaurant selector props */
  restaurantSelectorProps?: {
    title?: string;
    description?: string;
    emptyMessage?: string;
  };
  /** Custom CSS class for the container */
  className?: string;
}

export function SelectionWizard({
  onComplete,
  returnUrl,
  onRedirect,
  updateLastLogin = true,
  tenantSelectorProps,
  restaurantSelectorProps: locationSelectorProps,
  className
}: SelectionWizardProps) {
  const { data: authData } = useAuth();
  const { authUser } = authData || {};
  const { data: firestoreUser, isLoading: isUserLoading } = useGetUserByUid(authUser?.uid);
  const updateUserMutation = useUpdateUser();
  const { setSelection } = useSelection();
  const signOutMutation = useSignOut();

  // Sign out and hard-redirect to root so the app's auth guard takes over —
  // a user on the org picker must always be able to get out (never stranded
  // with no exit but clearing cache).
  const handleSignOut = () => {
    signOutMutation.mutate(undefined, {
      onSettled: () => {
        if (typeof window !== 'undefined') {
          window.location.href = '/';
        }
      },
    });
  };

  const [selectedTenant, setSelectedTenant] = React.useState<Tenant | null>(null);

  // Effect to update lastLogin time when the component mounts for an active user.
  useEffect(() => {
    if (updateLastLogin && firestoreUser && firestoreUser.uid && !firestoreUser.lastLogin) {
      logger.debug('Updating lastLogin for user', { uid: firestoreUser.uid });
      updateUserMutation.mutate({
        uid: firestoreUser.uid,
        userData: { lastLogin: new Date() },
      });
    }
  }, [firestoreUser, updateUserMutation, updateLastLogin]);

  const handleTenantSelect = (tenant: Tenant) => {
    setSelectedTenant(tenant);
  };

  const handleLocationSelect = async (locationRecord: LocationRecord) => {
    if (!selectedTenant) return;

    logger.debug('Restaurant selected', { restaurantName: locationRecord.name, restaurantGuid: locationRecord.guid });

    // Persist the selection in context
    setSelection(selectedTenant, locationRecord);

    const selectionData = {
      tenant: selectedTenant,
      restaurant: locationRecord,
      token: undefined as string | undefined
    };

    // Get auth token if needed for external redirect
    if (returnUrl || onComplete) {
      try {
        const tokenData = await getCurrentUserToken(true);
        if (tokenData) {
          selectionData.token = tokenData.token;
        }
      } catch (error) {
        logger.error('Failed to get auth token', error);
      }
    }

    // Handle external redirect with return URL
    if (returnUrl) {
      try {
        const url = new URL(returnUrl);
        if (selectionData.token) {
          url.searchParams.set('token', selectionData.token);
          url.searchParams.set('restaurantId', locationRecord.guid);
          url.searchParams.set('tenantId', selectedTenant?.guid || '');
        }

        const finalUrl = url.toString();
        if (onRedirect) {
          onRedirect(finalUrl);
        } else {
          window.location.href = finalUrl;
        }
        return;
      } catch (error) {
        logger.error('Failed to handle return URL redirect', error);
      }
    }

    // Call completion handler
    if (onComplete) {
      onComplete(selectionData);
    }
  };

  const handleBack = () => {
    setSelectedTenant(null);
  };

  return (
    <div className={className}>
      <div className="container mx-auto max-w-md p-4">
        {/* Progress indicator */}
        <div className="mb-6 text-center">
          <p className="text-sm text-muted-foreground">
            Step {!selectedTenant ? '1' : '2'} of 2
          </p>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle>
              {!selectedTenant ? 'Select Organization' : 'Select Location'}
            </CardTitle>
            <CardDescription>
              {!selectedTenant
                ? 'Choose your organization to access your workspace'
                : `Select a location within ${selectedTenant.displayName}`
              }
            </CardDescription>
          </CardHeader>

          <CardContent>
            {isUserLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Skeleton className="h-9 w-9 rounded" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : !selectedTenant ? (
              <TenantSelector
                onSelect={handleTenantSelect}
                {...tenantSelectorProps}
              />
            ) : (
              <LocationSelector
                tenant={selectedTenant}
                onSelect={handleLocationSelect}
                onBack={handleBack}
                {...locationSelectorProps}
              />
            )}
          </CardContent>
        </Card>

        <div className="mt-6 space-y-2 text-center">
          {authUser?.email && (
            <p className="text-sm text-muted-foreground">
              Signed in as{' '}
              <span className="font-medium text-foreground">
                {authUser.email}
              </span>
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            <button
              className="text-primary hover:underline disabled:opacity-60"
              onClick={handleSignOut}
              disabled={signOutMutation.isPending}
            >
              {signOutMutation.isPending ? 'Logging out…' : 'Log out'}
            </button>
            <span className="px-2">·</span>
            Need help?{' '}
            <button
              className="text-primary hover:underline"
              onClick={() => {
                logger.debug('Contact administrator clicked');
              }}
            >
              Contact your administrator
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
